import { type ApprovalRequestId, type UserInputQuestion } from "@cmmd-forge/contracts";
import { memo, useEffect, useEffectEvent, useRef, useState } from "react";
import { PaperclipIcon, XIcon } from "lucide-react";
import type { ComposerFileAttachment } from "../../composerDraftStore";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { type PendingUserInput } from "../../session-logic";
import {
  derivePendingUserInputProgress,
  type PendingUserInputDraftAnswer,
} from "../../pendingUserInput";
import { CheckIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { useComposerAttachmentIntake } from "./useComposerImageAttachments";

const NO_FILES_CHANGE = () => {};

interface PendingUserInputPanelProps {
  pendingUserInputs: PendingUserInput[];
  respondingRequestIds: ApprovalRequestId[];
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  error?: string | null;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  onFilesChange?: ((questionId: string, files: ComposerFileAttachment[]) => void) | undefined;
}

export const ComposerPendingUserInputPanel = memo(function ComposerPendingUserInputPanel({
  pendingUserInputs,
  respondingRequestIds,
  answers,
  questionIndex,
  error = null,
  onToggleOption,
  onAdvance,
  onFilesChange = NO_FILES_CHANGE,
}: PendingUserInputPanelProps) {
  if (pendingUserInputs.length === 0) return null;
  const activePrompt = pendingUserInputs[0];
  if (!activePrompt) return null;

  return (
    <ComposerPendingUserInputCard
      key={activePrompt.requestId}
      prompt={activePrompt}
      isResponding={respondingRequestIds.includes(activePrompt.requestId)}
      answers={answers}
      questionIndex={questionIndex}
      error={error}
      onToggleOption={onToggleOption}
      onAdvance={onAdvance}
      onFilesChange={onFilesChange}
    />
  );
});

const ComposerPendingUserInputCard = memo(function ComposerPendingUserInputCard({
  prompt,
  isResponding,
  answers,
  questionIndex,
  error,
  onToggleOption,
  onAdvance,
  onFilesChange = NO_FILES_CHANGE,
}: {
  prompt: PendingUserInput;
  isResponding: boolean;
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  error: string | null;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  onFilesChange?: ((questionId: string, files: ComposerFileAttachment[]) => void) | undefined;
}) {
  const progress = derivePendingUserInputProgress(prompt.questions, answers, questionIndex);
  const activeQuestion = progress.activeQuestion;
  const autoAdvance = usePendingUserInputAutoAdvance(onAdvance);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOptionSelection = usePendingUserInputSelection(
    activeQuestion,
    onToggleOption,
    autoAdvance,
  );

  useQuestionKeyboardShortcut(activeQuestion, isResponding, handleOptionSelection);

  const { attachments, attachmentIntake, fileError } = useQuestionAttachmentIntake({
    prompt,
    answers,
    question: activeQuestion,
    isResponding,
    onFilesChange,
  });

  if (!activeQuestion) {
    return null;
  }

  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {prompt.questions.length > 1 ? (
            <span className="flex h-5 items-center rounded-md bg-muted/60 px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground/60">
              {questionIndex + 1}/{prompt.questions.length}
            </span>
          ) : null}
          <span className="text-[11px] font-semibold tracking-widest text-muted-foreground/50 uppercase">
            {activeQuestion.header}
          </span>
        </div>
      </div>
      <p className="mt-1.5 text-sm text-foreground/90">{activeQuestion.question}</p>
      {error || fileError ? (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {error ?? fileError}
        </p>
      ) : null}
      {activeQuestion.multiSelect ? (
        <p className="mt-1 text-xs text-muted-foreground/65">Select one or more options.</p>
      ) : null}
      <PendingUserInputOptions
        question={activeQuestion}
        selectedOptionLabels={progress.selectedOptionLabels}
        disabled={isResponding}
        onSelect={handleOptionSelection}
      />
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            event.currentTarget.value = "";
            attachmentIntake.addFiles(files);
          }}
        />
        <button
          type="button"
          disabled={isResponding}
          onClick={() => fileInputRef.current?.click()}
          className="flex min-h-11 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted/40"
        >
          <PaperclipIcon className="size-3.5" /> Attach files
        </button>
        {attachments.map((attachment) => (
          <span
            key={attachment.id}
            className="flex min-h-11 items-center gap-1 rounded-md bg-muted/40 px-2 text-xs"
          >
            <span className="max-w-32 truncate">{attachment.name}</span>
            <button
              type="button"
              aria-label={`Remove ${attachment.name}`}
              onClick={() => {
                onFilesChange(
                  activeQuestion.id,
                  attachments.filter((file) => file.id !== attachment.id),
                );
                void releaseUnreferencedComposerFile(attachment.id);
              }}
            >
              <XIcon className="size-3.5" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
});

function PendingUserInputOptions(props: {
  readonly question: UserInputQuestion;
  readonly selectedOptionLabels: readonly string[];
  readonly disabled: boolean;
  readonly onSelect: (questionId: string, optionLabel: string) => void;
}) {
  return (
    <div className="mt-3 space-y-1">
      {props.question.options.map((option, index) => (
        <button
          key={`${props.question.id}:${option.label}`}
          type="button"
          disabled={props.disabled}
          onClick={() => props.onSelect(props.question.id, option.label)}
          className={cn(
            "group flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all duration-150",
            props.selectedOptionLabels.includes(option.label)
              ? "border-info-border bg-info-surface text-foreground"
              : "border-transparent bg-muted/20 text-foreground/80 hover:bg-muted/40 hover:border-border/40",
            props.disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <kbd className="flex size-5 shrink-0 items-center justify-center rounded bg-muted/40 text-[11px] font-medium tabular-nums text-muted-foreground/50">
            {index + 1}
          </kbd>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium">{option.label}</span>
            {option.description && option.description !== option.label ? (
              <span className="ml-2 text-xs text-muted-foreground/50">{option.description}</span>
            ) : null}
          </div>
          {props.selectedOptionLabels.includes(option.label) ? (
            <CheckIcon className="size-3.5 shrink-0 text-info-foreground" />
          ) : null}
        </button>
      ))}
    </div>
  );
}

function usePendingUserInputAutoAdvance(onAdvance: () => void) {
  const timerRef = useRef<number | null>(null);
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );
  return { timerRef, onAdvanceRef };
}

function usePendingUserInputSelection(
  question: UserInputQuestion | null,
  onToggle: (questionId: string, optionLabel: string) => void,
  autoAdvance: {
    readonly timerRef: { current: number | null };
    readonly onAdvanceRef: { current: () => void };
  },
) {
  return useEffectEvent((questionId: string, optionLabel: string) => {
    onToggle(questionId, optionLabel);
    if (question?.multiSelect) return;
    if (autoAdvance.timerRef.current !== null) window.clearTimeout(autoAdvance.timerRef.current);
    autoAdvance.timerRef.current = window.setTimeout(() => {
      autoAdvance.timerRef.current = null;
      autoAdvance.onAdvanceRef.current();
    }, 200);
  });
}

function useQuestionKeyboardShortcut(
  question: UserInputQuestion | null,
  isResponding: boolean,
  onSelect: (questionId: string, optionLabel: string) => void,
): void {
  useEffect(() => {
    if (!question || isResponding) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      const option = keyboardOption(question, event);
      if (!option) return;
      event.preventDefault();
      onSelect(question.id, option.label);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [question, isResponding, onSelect]);
}

function useQuestionAttachmentIntake(input: {
  readonly prompt: PendingUserInput;
  readonly answers: Record<string, PendingUserInputDraftAnswer>;
  readonly question: UserInputQuestion | null;
  readonly isResponding: boolean;
  readonly onFilesChange: (questionId: string, files: ComposerFileAttachment[]) => void;
}) {
  const attachments = input.question ? (input.answers[input.question.id]?.attachments ?? []) : [];
  const [fileError, setFileError] = useState<string | null>(null);
  const attachmentIntake = useComposerAttachmentIntake({
    images: [],
    files: Object.values(input.answers).flatMap((answer) => answer.attachments ?? []),
    scopeKey: `${input.prompt.requestId}:${input.question?.id ?? "none"}`,
    fileOnly: true,
    isDisabled: () => input.isResponding || !input.question,
    onAddImages: () => {},
    onAddFiles: (files) => {
      if (input.question) input.onFilesChange(input.question.id, [...attachments, ...files]);
    },
    onError: setFileError,
  });
  return { attachments, attachmentIntake, fileError };
}

function keyboardOption(question: UserInputQuestion, event: globalThis.KeyboardEvent) {
  if (event.metaKey || event.ctrlKey || event.altKey || isEditableTarget(event.target)) return null;
  const index = Number.parseInt(event.key, 10) - 1;
  return index >= 0 && index < 9 ? (question.options[index] ?? null) : null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement &&
      Boolean(target.closest('[contenteditable]:not([contenteditable="false"])')))
  );
}
